/**
 * Procedure Service — fetches SID/STAR/Approach procedures from the CIFP
 * database via OGC API Features, and charts from the chart_api service.
 */
import type { Feature } from 'geojson';
import {
  GPKG_COLLECTION_CIFP_PROCEDURES,
  buildChartOgcCollectionUrl,
  buildChartPdfUrl,
  buildOgcCollectionUrl,
} from '../config/environment';
import { getAuthHeader } from './AuthContext';
import type {
  Procedure,
  ProcedureType,
  ApproachType,
  ChartMetadata,
} from '../types/airport';

/** Properties on CIFP procedure features */
interface CifpProcedureProperties {
  airport_identifier?: string;
  procedure_identifier?: string;
  procedure_type?: string;
  route_type?: string;
  transition_identifier?: string;
  runway_identifier?: string;
  [key: string]: unknown;
}

/** Properties on d-TPP approach plate quad records */
interface ApproachPlateProperties {
  apt_ident?: string;
  airport_ident?: string;
  chart_code?: string;
  chart_id?: string;
  chart_name?: string;
  chart_type?: string;
  procuid?: string;
  procedure_id?: string;
  pdf_name?: string;
  is_georef?: string;
  georeferenced?: boolean;
  cycle?: string;
  [key: string]: unknown;
}

export class ProcedureService {
  /**
   * Fetch all procedures (SIDs, STARs, approaches) for an airport from CIFP.
   */
  async getProcedures(airportIdentifier: string): Promise<Procedure[]> {
    const id = airportIdentifier.trim().toUpperCase();

    try {
      // Fetch from OGC procedure collection
      const url = buildOgcCollectionUrl(GPKG_COLLECTION_CIFP_PROCEDURES);
      const res = await fetch(
        `${url}/items?filter=${encodeURIComponent(
          `airport_identifier='${id}'`,
        )}&filter-lang=cql2-text&limit=500&f=json`,
        { headers: getAuthHeader() },
      );
      if (!res.ok) return [];
      const fc = await res.json();

      return this.aggregateProcedures((fc.features ?? []) as Feature[]);
    } catch (err) {
      console.warn('ProcedureService: fetch failed', err);
      return [];
    }
  }

  /**
   * Fetch airport charts (diagram, approach plates, SID/STAR charts) from
   * chart_api. Uses the d-TPP approach_plates OGC collection keyed by
   * airport identifier.
   */
  async getCharts(airportIdentifier: string): Promise<ChartMetadata[]> {
    const id = airportIdentifier.trim().toUpperCase();

    try {
      const url = buildChartOgcCollectionUrl('approach_plates');
      const res = await fetch(
        `${url}/items?filter=${encodeURIComponent(
          `apt_ident='${id}'`,
        )}&filter-lang=cql2-text&limit=100&f=json`,
        { headers: getAuthHeader() },
      );
      if (!res.ok) return [];
      const fc = await res.json();

      return (fc.features ?? [])
        .map((f: Feature) => this.mapChart(f.properties as ApproachPlateProperties))
        .filter((c: ChartMetadata | null): c is ChartMetadata => c !== null);
    } catch (err) {
      console.warn('ProcedureService: chart fetch failed', err);
      return [];
    }
  }

  /**
   * Build a PDF URL for a chart given its metadata.
   */
  buildChartPdfUrl(cycle: string, pdfName: string): string {
    return buildChartPdfUrl(cycle, pdfName);
  }

  // ─── Private helpers ──────────────────────────────────────────────────

  /**
   * Aggregate raw CIFP records by procedure_identifier (CIFP exposes one
   * record per leg). Group by procedure and collect transitions.
   */
  private aggregateProcedures(features: Feature[]): Procedure[] {
    const groups = new Map<string, CifpProcedureProperties[]>();

    for (const feat of features) {
      const props = feat.properties as CifpProcedureProperties;
      const key = `${props.procedure_type ?? ''}-${props.procedure_identifier ?? ''}`;
      if (!key.trim()) continue;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(props);
    }

    const procedures: Procedure[] = [];
    for (const records of groups.values()) {
      const proc = this.buildProcedure(records);
      if (proc) procedures.push(proc);
    }

    // Sort: approaches first, then SIDs, then STARs
    return procedures.sort((a, b) => {
      const typeOrder: Record<ProcedureType, number> = { APPROACH: 0, SID: 1, STAR: 2 };
      const diff = typeOrder[a.type] - typeOrder[b.type];
      if (diff !== 0) return diff;
      return a.identifier.localeCompare(b.identifier);
    });
  }

  private buildProcedure(records: CifpProcedureProperties[]): Procedure | null {
    if (records.length === 0) return null;
    const first = records[0];
    const procId = first.procedure_identifier;
    if (!procId) return null;

    const type = this.inferProcedureType(first.procedure_type, first.route_type);
    const approachType = type === 'APPROACH' ? this.inferApproachType(procId) : undefined;
    const runway = this.inferRunway(procId, first.runway_identifier);

    const transitions = Array.from(
      new Set(
        records
          .map((r) => r.transition_identifier)
          .filter((t): t is string => Boolean(t) && t !== procId),
      ),
    );

    return {
      identifier: procId,
      type,
      approachType,
      runway,
      transitions: transitions.length > 0 ? transitions : undefined,
    };
  }

  private inferProcedureType(
    procType?: string,
    routeType?: string,
  ): ProcedureType {
    const t = (procType ?? routeType ?? '').toUpperCase();
    if (t.includes('SID') || t.includes('DEPARTURE')) return 'SID';
    if (t.includes('STAR') || t.includes('ARRIVAL')) return 'STAR';
    return 'APPROACH';
  }

  private inferApproachType(procId: string): ApproachType | undefined {
    const id = procId.toUpperCase();
    if (id.startsWith('I') || id.includes('ILS')) return 'ILS';
    if (id.includes('RNAV') || id.includes('RNP')) return 'RNAV';
    if (id.startsWith('R') && /R\d/.test(id)) return 'RNAV';
    if (id.startsWith('L') || id.includes('LOC')) return 'LOC';
    if (id.startsWith('V') || id.includes('VOR')) return 'VOR';
    if (id.startsWith('N') || id.includes('NDB')) return 'NDB';
    if (id.includes('GPS')) return 'GPS';
    if (id.includes('LDA')) return 'LDA';
    if (id.includes('SDF')) return 'SDF';
    if (id.includes('TACAN')) return 'TACAN';
    if (id.includes('VIS')) return 'Visual';
    return undefined;
  }

  private inferRunway(procId: string, rwyField?: string): string | undefined {
    if (rwyField) return rwyField.replace(/^RW/, '');
    // Try to extract runway from procedure name (e.g., "ILS RWY 25L")
    const match = procId.match(/(?:RW|RWY)\s*(\d{1,2}[LRC]?)/i);
    if (match) return match[1];
    // Or from trailing pattern like "I25L"
    const trailing = procId.match(/(\d{2}[LRC]?)$/);
    if (trailing) return trailing[1];
    return undefined;
  }

  private mapChart(props: ApproachPlateProperties): ChartMetadata | null {
    if (!props.chart_id && !props.pdf_name) return null;

    const chartType = this.normalizeChartType(props.chart_type ?? props.chart_code);

    return {
      id: props.chart_id ?? props.pdf_name ?? '',
      name: props.chart_name ?? props.pdf_name ?? 'Unknown chart',
      type: chartType,
      procedureId: props.procedure_id ?? props.procuid,
      pdfUrl: props.pdf_name && props.cycle
        ? this.buildChartPdfUrl(props.cycle, props.pdf_name)
        : undefined,
      georeferenced: props.georeferenced ?? props.is_georef === 'Y',
    };
  }

  private normalizeChartType(type?: string): ChartMetadata['type'] {
    const t = (type ?? '').toUpperCase();
    if (t.includes('APD') || t.includes('DIAGRAM')) return 'APD';
    if (t.includes('DP') || t.includes('DEPARTURE')) return 'DP';
    if (t.includes('STAR')) return 'STAR';
    if (t.includes('MIN')) return 'MIN';
    return 'IAP';
  }
}
